import { useState, useEffect } from 'react';
import BlSelect from './components/BlSelect';
import {
  SearchOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  CustomerServiceOutlined,
  RightOutlined,
  ToolOutlined,
  LinkOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  MessageOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
  MobileOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import Layout from './components/Layout';
import { governorates, professionTypeOptions } from '../hooks';
import { supabase } from '../supabaseClient';
import './BatilinkLanding.css';
import './HeroSearch.css';
import './HowItWorks.css';
import appStoreImg from '../assets/images/app-store.svg';
import batilinkImg from '../assets/images/batilink.png';
import plombierImg from '../assets/images/plombier.jpg';
import electricienImg from '../assets/images/homme-technicien-electrique-travaillant-dans-central-electrique-fusibles_169016-24062.jpg';
import peintureImg from '../assets/images/peinture.jpg';
import maconnerieImg from '../assets/images/macon.jpg';
import menuiserieImg from '../assets/images/menuisier.jpg';
import fournisseurImg from '../assets/images/fournisseur.jpg';
import ingenieurImg from '../assets/images/ingenieur-beton-arme.jpg';
import promoteurImg from '../assets/images/promoteur_immobilier.webp';
import designerImg from '../assets/images/Designer_d’intérieur.jpg';
import facadeImg from '../assets/images/Façadier.jpg';
import hydrauliqueImg from '../assets/images/ingenieur-hydraulique.jpg';
import maitreOuvrageImg from '../assets/images/Maître d\'ouvrage.webp';

import ScrollToTop from './components/ScrollToTop';
import SponsorsSection from './SponsorsSection';
import ContactSection from './ContactSection';
import qrCodeBatilinkImg from '../assets/qr-code-batilink.png';
import webappImg from '../assets/images/webapp.png';
import { APP_URLS } from '../config/appConfig';

function BatilinkLanding() {
  const history = useHistory();
  const [showAllServices, setShowAllServices] = useState(false);
  const [selectedService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRegion, setSearchRegion] = useState('');
  const [searchProfession, setSearchProfession] = useState('');
  const [professionsList, setProfessionsList] = useState([]);
  const [proCount, setProCount] = useState(0);
  const [specialtiesCount, setSpecialtiesCount] = useState(0);

  // Somme des professions de toutes les tables de types (architect, supplier, etc.)
  // via une fonction SQL (RPC) qui contourne le RLS et ne renvoie que le total.
  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase.rpc('count_professions');
      if (!active) return;
      if (!error && data != null) setSpecialtiesCount(Number(data) || 0);
      else if (error) console.warn('[count_professions]', error.message);
    })();
    return () => { active = false; };
  }, []);

  // Charge les métiers (profession) distincts pour le menu "Tous types"
  useEffect(() => {
    let active = true;
    (async () => {
      const { data, count } = await supabase
        .from('profiles')
        .select('profession', { count: 'exact' })
        .eq('role', 'professional')
        .eq('is_activated', true);
      if (!active) return;
      setProCount(count || (data || []).length);
      const uniq = [...new Set((data || []).map((r) => r.profession).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      setProfessionsList(uniq);
    })();
    return () => { active = false; };
  }, []);

  // Lance la recherche -> page dédiée /recherche avec les filtres en query string
  const handleHeroSearch = (e) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (searchRegion) params.set('region', searchRegion);
    if (searchProfession) params.set('profession', searchProfession);
    history.push(`/recherche?${params.toString()}`);
  };

  // Statistics data (unused in this page)

  // Professional types

  // Construction service categories using local images from assets
  const serviceCategories = [
    { name: 'Plombier', image: plombierImg, alt: 'Plombier' },
    { name: 'Électricien', image: electricienImg, alt: 'Électricien' },
    { name: 'Peintre', image: peintureImg, alt: 'Peintre' },
    { name: 'Maçon', image: maconnerieImg, alt: 'Maçon' },
    { name: 'Menuisier', image: menuiserieImg, alt: 'Menuisier' },
    { name: 'Fournisseur', image: fournisseurImg, alt: 'Fournisseur' },
    { name: 'Ingénieur béton armé', image: ingenieurImg, alt: 'Ingénieur béton armé' }
    ,{ name: 'Promoteur immobilier', image: promoteurImg, alt: 'Promoteur immobilier' }
    ,{ name: "Designer d'intérieur", image: designerImg, alt: "Designer d'intérieur" }
    ,{ name: 'Façadier', image: facadeImg, alt: 'Façadier' }
    ,{ name: "Ingénieur hydraulique", image: hydrauliqueImg, alt: "Ingénieur hydraulique" }
    ,{ name: "Maître d'ouvrage", image: maitreOuvrageImg, alt: "Maître d'ouvrage" }
  ];

  // Trust features
  const trustFeatures = [
    {
      icon: <CheckCircleOutlined />,
      title: 'Professionnels Vérifiés',
      description: 'Tous nos prestataires sont qualifiés et leur identité est vérifiée pour votre sécurité.'
    },
    {
      icon: <CustomerServiceOutlined />,
      title: 'Service Client 7j/7',
      description: 'Notre équipe vous accompagne de la mise en relation jusqu\'à la fin des travaux.'
    },
    {
      icon: <SafetyOutlined />,
      title: 'Gratuite pour les clients',
      description: 'Profitez gratuitement de Batilink tout en bénéficiant des meilleurs tarifs du marché pour vos services'
    }
  ];

  // How it works steps
  const howItWorksSteps = [
    {
      number: '1',
      title: 'Cherchez',
      description: 'Décrivez votre besoin, choisissez votre région et filtrez par type de professionnel.',
      icon: <SearchOutlined />
    },
    {
      number: '2',
      title: 'Contactez',
      description: 'Consultez les profils, portfolios et avis clients. Contactez directement via l\'app pour un échange rapide.',
      icon: <MessageOutlined />
    },
    {
      number: '3',
      title: 'Construisez',
      description: 'Confiez votre projet à un professionnel vérifié. Donnez votre avis à la fin.',
      icon: <ToolOutlined />
    }
  ];

  // reveal sections and cards on scroll using IntersectionObserver
  useEffect(() => {
    // Check user preference and viewport size
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSmall = typeof window !== 'undefined' && window.innerWidth <= 720;
    const shouldAnimate = !prefersReduced && !isSmall;

    const sections = document.querySelectorAll('.batilink-landing section');
    const cards = document.querySelectorAll('.service-card');

    if (!shouldAnimate) {
      // Immediately reveal everything (no transitions) for performance/safety
      sections.forEach(s => s.classList.add('revealed'));
      cards.forEach(c => c.classList.add('revealed'));
      return;
    }

    // if all services were just shown, make sure newly rendered cards are visible quickly
    if (showAllServices) {
      cards.forEach((c) => c.classList.add('revealed'));
      // still continue to observe remaining ones for staggered effect
    }

    const root = null;
    const options = { root, rootMargin: '0px 0px -10% 0px', threshold: 0.12 };

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = entry.target.dataset.revealIndex;
          if (typeof idx !== 'undefined') {
            const delay = Number(idx) * 60;
            entry.target.style.transitionDelay = `${delay}ms`;
          }
          entry.target.classList.add('revealed');
          obs.unobserve(entry.target);
        }
      });
    }, options);

    sections.forEach(s => observer.observe(s));
    cards.forEach((c, i) => {
      c.dataset.revealIndex = i;
      observer.observe(c);
    });

    return () => observer.disconnect();
  }, [showAllServices]);

  return (
    <Layout>
      <div className="batilink-landing">
        {/* Hero Section - ijeni style */}
        <section className="hero-section" id="accueil">
          <div className="container">
            <div className="hero-content">

              <h1 className="hero-title">
                Le service dont vous avez besoin,<br />
                <span className="hero-title-accent">en une minute chrono !</span>
              </h1>
              <p className="hero-subtitle">
                La meilleure plateforme  du BTP en Tunisie. Connectez-vous avec les meilleurs professionnels en quelques clics.
              </p>

              <form className="bl-search" onSubmit={handleHeroSearch}>
                <div className="bl-search-field bl-search-text">
                  <ShopOutlined className="bl-search-ic" />
                  <input
                    type="search"
                    id="heroProSearch"
                    name="heroProSearch"
                    placeholder="Maçon, électricien, plombier.."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <span className="bl-search-div" />

                <div className="bl-search-field">
                  <BlSelect
                    ariaLabel="Région"
                    icon={<EnvironmentOutlined />}
                    placeholder="Toutes régions"
                    value={searchRegion}
                    onChange={setSearchRegion}
                    options={governorates}
                  />
                </div>

                <span className="bl-search-div" />

                <div className="bl-search-field">
                  <BlSelect
                    ariaLabel="Type de professionnel"
                    icon={<SearchOutlined />}
                    placeholder="Tous types"
                    value={searchProfession}
                    onChange={setSearchProfession}
                    options={professionTypeOptions(professionsList)}
                  />
                </div>

                <button type="submit" className="bl-search-btn">
                  <SearchOutlined /> Rechercher
                </button>
              </form>

              {/* Statistiques sous la recherche */}
              {/* <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-ic"><TeamOutlined /></span>
                  <div className="hero-stat-body">
                    <span className="hero-stat-num">{proCount > 0 ? `${proCount}+` : '—'}</span>
                    <span className="hero-stat-label">Professionnels</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-ic"><EnvironmentOutlined /></span>
                  <div className="hero-stat-body">
                    <span className="hero-stat-num">{governorates.length}</span>
                    <span className="hero-stat-label">Gouvernorats couverts</span>
                  </div>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-ic"><ToolOutlined /></span>
                  <div className="hero-stat-body">
                    <span className="hero-stat-num">{specialtiesCount > 0 ? `${specialtiesCount}+` : '—'}</span>
                    <span className="hero-stat-label">Spécialités</span>
                  </div>
                </div>
              </div> */}

              <div className="hero-install-cta" style={{ marginTop: 70 }}>
                <div className="install-card">
                  <div className="install-card-glow"></div>
                  <div className="install-icon-wrap">
                    <DownloadOutlined className="install-icon" />
                  </div>
                  <div className="install-card-body">
                    <h3 className="install-card-title">
                      Téléchargez l'application <span className="install-brand">BATILINK</span>
                    </h3>
                    <p className="install-card-sub">
                      Application disponible sur Android et iOS 
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hero-install-btn"
                    onClick={() => {
                      const el = document.getElementById('app-mobile');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <span>Installer maintenant</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      
        {/* How It Works (déplacé sous le hero) */}
        <section className="how-it-works-section how-it-works-section-bg">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Comment ça marche ?</h2>
              <p className="section-subtitle">Trouvez et contactez un professionnel BTP en 3 étapes simples.</p>
            </div>

            <div className="bl-hiw">
              <span className="bl-hiw-line" />
              {howItWorksSteps.map((step, index) => (
                <div key={index} className="bl-hiw-step">
                  <div className="bl-hiw-icon">
                    {step.icon}
                    <span className="bl-hiw-num">{step.number}</span>
                  </div>
                  <h3 className="bl-hiw-title">{step.title}</h3>
                  <p className="bl-hiw-desc">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section (Qui sommes-nous) — masquée à la demande
        <section className="about-section about-section-top" id="about-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Qui Sommes-Nous ?</h2>
              <p className="section-subtitle">
                Découvrez BATILINK
              </p>
            </div>

            <div className="about-content">
              <div className="about-text-wrapper">
                <p className="about-text-simple">
                  <strong>BATILINK</strong> est la meilleure plateforme digitale qui connecte tous les acteurs du BTP en Tunisie.
                  L'objectif de BATILINK est de simplifier l'accès aux services du bâtiment en Tunisie tout en offrant aux professionnels une opportunité unique de digitaliser leur activité, de gagner en visibilité et de développer leur réseau de clients.
                </p>
              
                <p className="about-text-simple">
                  Nous sélectionnons les meilleurs professionnels pour garantir un service de qualité supérieure. Notre plateforme offre une expérience rapide, fiable et transparente pour tous vos besoins en construction et rénovation.
                </p>
              </div>
            </div>

            <div className="trust-grid" style={{ marginTop: '48px' }}>
              <div className="trust-card">
                <div className="trust-icon"><ThunderboltOutlined /></div>
                <h3 className="trust-title">Rapidité</h3>
                <p className="trust-description">Trouvez un professionnel en moins d'une minute</p>
              </div>
              <div className="trust-card">
                <div className="trust-icon"><StarFilled /></div>
                <h3 className="trust-title">Qualité</h3>
                <p className="trust-description">Des professionnels vérifiés et notés par la communauté</p>
              </div>
              <div className="trust-card">
                <div className="trust-icon"><TrophyOutlined /></div>
                <h3 className="trust-title">Excellence</h3>
                <p className="trust-description">Les meilleurs professionnels pour un service de qualité supérieure</p>
              </div>
              <div className="trust-card">
                <div className="trust-icon"><HeartOutlined /></div>
                <h3 className="trust-title">Satisfaction</h3>
                <p className="trust-description">Votre satisfaction est notre priorité absolue</p>
              </div>
            </div>
          </div>
        </section>
        */}


    {/* Services Section */}
      <section className="services-section services-section-bg" id="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Services les plus demandés</h2>
            <p className="section-subtitle">Trouvez votre prestataire idéal en quelques clics</p>
          </div>

          <div className="services-grid">
            {(
              showAllServices ? serviceCategories : serviceCategories.slice(0, 8)
            ).map((service, index) => (
              <div key={index} className="service-card" style={selectedService === service.name ? { boxShadow: '0 6px 20px rgba(0,0,0,0.15)', transform: 'translateY(-4px)', border: '2px solid #fdd119' } : {}}>
                <div className="service-image-wrapper" style={{ background: '#f4f6f8', borderRadius: 8, overflow: 'hidden' }}>
                  <img 
                    src={service.image} 
                    alt={service.alt} 
                    className="service-image"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 12px', background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)', color: '#fff', fontWeight: 700, textAlign: 'center' }}>{service.name}</div>
                </div>
                <h3 className="service-name" style={{ marginTop: 10 }}>{service.name}</h3>
              </div>
            ))}
          </div>

          <div className="view-all-wrapper">
            <button
              className="view-all-btn"
              onClick={() => setShowAllServices(prev => !prev)}
            >
              {showAllServices ? 'Voir moins' : 'Voir tous les services'} <RightOutlined />
            </button>
          </div>
        </div>
      </section>

   

        {/* Trust Features */}
        {/* <section className="trust-section trust-section-bg">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Pourquoi Choisir BATILINK ?</h2>
              <p className="section-subtitle">Des services et professionnels de confiance</p>
            </div>

            <div className="trust-grid">
              {trustFeatures.map((feature, index) => (
                <div key={index} className="trust-card">
                  <div className="trust-icon">{feature.icon}</div>
                  <h3 className="trust-title">{feature.title}</h3>
                  <p className="trust-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Promotions Section - Modern Design */}
        <section className="promotions-section promotions-section-bg" id="promos">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Promotions Exclusives</h2>
              <p className="section-subtitle">
                Profitez des meilleures offres
              </p>
            </div>

            <div className="promos-content-wrapper">
              <div className="promos-cards-grid">
                <div className="promo-card">
                  <div className="promo-icon"><GiftOutlined /></div>
                  <h3 className="promo-title">Offres Exclusives</h3>
                  <p className="promo-description">
                    Découvrez nos <strong>promotions exclusives</strong> sur des produits et services de qualité,
                    proposés par nos fournisseurs partenaires.
                  </p>
                </div>

                <div className="promo-card">
                  <div className="promo-icon"><SafetyCertificateOutlined /></div>
                  <h3 className="promo-title">Produits Vérifiés</h3>
                  <p className="promo-description">
                    Des offres vérifiées pour tous vos besoins en BTP. Matériaux de construction, équipements et plus encore.
                  </p>
                </div>

                <div className="promo-card">
                  <div className="promo-icon"><MobileOutlined /></div>
                  <h3 className="promo-title">Accès en Temps Réel</h3>
                  <p className="promo-description">
                    Téléchargez l'application mobile pour accéder à toutes les promotions en temps réel et ne manquez aucune offre !
                  </p>
                </div>
              </div>

              <div className="promos-cta">
                <div className="promos-cta-content">
                  <span className="promos-cta-emoji">💡</span>
                  <p className="promos-cta-text">
                    Qu’attends-tu ? Installe l’application dès maintenant et profite de nos meilleurs services et promotions !
                  </p>
                </div>
                <button
                  type="button"
                  className="promos-cta-btn"
                  onClick={() => {
                    const el = document.getElementById('app-mobile');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <DownloadOutlined /> Télécharger l'application
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* App Download Section */}
        <section className="app-download-section app-download-section-bg" id="app-mobile">
          <div className="container">
            <div className="app-content">
              <div className="app-text">
                <h2 className="app-title">Plus pratique avec l'application</h2>
                <p className="app-description">
                  Téléchargez l'application pour une expérience encore plus simple et rapide.
                </p>
                <div className="app-buttons">

                  <a href={APP_URLS.GOOGLE_PLAY} target="_blank" rel="noopener noreferrer" className="app-btn google-play">
                    <div>
                      <span className="app-label">DISPONIBLE SUR</span>
                      <span className="app-store-name">Google Play</span>
                    </div>
                  </a>
                  <a href={APP_URLS.APP_STORE} target="_blank" rel="noopener noreferrer" className="app-btn app-store">
                    <img src={appStoreImg} alt="App Store" />
                    <div>
                      <span className="app-label">Télécharger dans</span>
                      <span className="app-store-name">l'App Store</span>
                    </div>
                  </a>
                </div>
              </div>
              <div className="app-qr-code">
                <div className="qr-code-container">
                  <img
                    src={qrCodeBatilinkImg}
                    alt="QR Code pour télécharger l'application BATILINK"
                    className="qr-code-img"
                  />
                  <div className="qr-code-logo">
                    <span style={{ color: "#222", fontWeight: 700, fontFamily: "Bruno Ace SC, sans-serif", fontSize: '12px' }}>BATI</span>
                    <span style={{ color: "#FDD119", fontWeight: 700, fontFamily: "Bruno Ace SC, sans-serif", fontSize: '12px' }}>LINK</span>
                  </div>
                </div>
              </div>
              <div className="app-image">
                <img
                  src={batilinkImg}
                  alt="Mobile app mockup"
                  className="app-phone-img"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Web Version Section */}
        <section className="web-version-section web-version-section-bg">
          <div className="container">
             <div className="section-header">
                  <h2 className="section-title">Version Web Disponible</h2>
                  <p className="section-subtitle">Accédez à BATILINK depuis votre ordinateur</p>
                </div>
            <div className="web-version-wrapper">
              <div className="web-version-text-content">
                <div className="web-version-description-content">

                  <div className="web-version-features" style={{marginTop: '20px'}}>
                      <p className="web-version-intro">
                        Découvrez notre plateforme web complète conçue pour vous offrir une expérience fluide et intuitive sur votre ordinateur. Accédez à toutes les fonctionnalités de BATILINK et gérez vos projets de construction avec facilité.
                      </p>
                  </div>

                  <a href={APP_URLS.FLUTTER_WEB_APP} target="_blank" rel="noopener noreferrer" className="web-version-cta-btn" style ={{marginTop: '20px', margin: '20px auto 0 auto', display: 'flex'}}>
                    <LinkOutlined /> Accédez à la Version Web
                  </a>
                </div>
              </div>


                <div style={{width: '100%', maxWidth: 700, margin: '0 auto'}}>
                  <img
                    src={webappImg}
                    alt="BATILINK Web Application"
                  />
                </div>

            </div>
          </div>
        </section>

        {/* Sponsors Section */}
        <SponsorsSection />

        {/* Contact Section */}
        <ContactSection />

        {/* Partners Information Section */}
        {/* <section className="partners-info-section partners-info-section-bg">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Pourquoi Devenir Partenaire BATILINK ?</h2>
              <p className="section-subtitle">Rejoignez notre réseau c'est devenir un membre actif de notre communauté</p>
            </div>

            <div className="partners-benefits-grid">
              <BenefitCard 
                icon={<TeamOutlined />}
                title="Attirez des professionnels qualifiés"
                description="Recrutez facilement des artisans et experts du bâtiment grâce à notre plateforme. Vous connectez les bons profils à BATILINK."
              />
              <BenefitCard 
                icon={<CustomerServiceOutlined />}
                title="Un partenariat gagnant-gagnant"
                description="Aucun investissement, aucune prise de risque. Plus vous recommandez, plus vous gagnez."
              />
              <BenefitCard 
                icon={<DollarOutlined />}
                title="Transformez votre réseau en revenus"
                description="Profitez d’un gain à chaque inscription. Chaque professionnel que vous amenez vous rapporte."
              />
              <BenefitCard 
                icon={<SafetyOutlined />}
                title="Participez à une plateforme en croissance"
                description="BATILINK révolutionne le secteur du bâtiment. Soyez aux premières loges d'une croissance rapide."
              />
            </div>
          </div>
        </section> */}

  {/* Partner CTA Section */}
  {/* <section id="contact" className="partner-cta-section partner-cta-section-bg">
          <div className="container">
            <div className="partner-cta-content">
              <div className="partner-cta-text">
                <h2 className="partner-cta-title">Prêt à Devenir Partenaire ?</h2>
                <p className="partner-cta-description">
                  Rejoignez BATILINK dès aujourd’hui et devenez partenaire. Qu’attendez-vous ? Contactez-nous dès maintenant !
                </p>
                
                <PartnerContactForm buttonText="Contactez Nous" />

                <p className="partner-cta-note">
                  Ou contactez-nous directement au <a href={APP_URLS.WHATSAPP} target="_blank" rel="noopener noreferrer" className="phone-link-inline">+216  36 722 922</a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Scroll to Top Button */}
        <ScrollToTop />
      </div>
    </Layout>
  );
}

export default BatilinkLanding;